import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from 'src/components/ui/button';
import { ProductsType } from 'src/constants/products';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useCreateConfigurationSettingsMutation,
  useDeployConfigurationSettingsMutation,
} from 'src/services';
import { useAppSelector, useAppDispatch } from 'src/store';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { CustomTemplateSelect } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStep4Page/CustomTemplateSelect/CustomTemplateSelect';
import { PlateEditor } from 'src/components/PlateEditor/PlateEditor';
import { getCreateProjectStepPath } from 'src/pages/ProjectPage/CreateProjectSteps/createProjectStepPath';
import type { CustomTemplateOption } from 'src/pages/ConfigurationPage/SystemPromptTab/SystemPromptTemplateSelect/SystemPromptTemplateSelect';
import type { ManualPromptFormValues } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStep4Page/CustomTemplateSelect/CustomTemplateSelect';
import type { CustomTemplate } from 'src/services/configurationService';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const CreateProjectManualStep4 = ({
  customTemplatesData,
}: {
  customTemplatesData?: CustomTemplate[];
}) => {
  const { id, step } = useParams();
  const [searchParams] = useSearchParams();
  const searchType = searchParams.get('type');
  const navigate = useNavigate();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const projectTypeVar = projectType || searchType;
  const [customTemplateOptions, setCustomTemplateOptions] = useState<CustomTemplateOption[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<CustomTemplateOption | null>(null);
  const dispatch = useAppDispatch();
  const [initialPlateValue, setInitialPlateValue] = useState('');
  const [
    createConfigurationSettings,
    { data: createConfigurationSettingsData, isLoading: isLoadingCreateConfigurationSettings },
  ] = useCreateConfigurationSettingsMutation();
  const [deployConfigurationSettings, { isLoading: isLoadingDeployConfigurationSettings }] =
    useDeployConfigurationSettingsMutation();

  const { control, setValue, handleSubmit } = useForm<ManualPromptFormValues>({
    defaultValues: {
      text: '',
      template_id: '',
    },
    resolver: formResolver(
      object({
        text: stringScheme({ required: true }),
      }),
    ),
  });

  useLoaderWhile(isLoadingCreateConfigurationSettings, isLoadingDeployConfigurationSettings);

  useEffect(() => {
    if (customTemplatesData) {
      const options = customTemplatesData.map((customTemplate) => {
        return {
          id: `${customTemplate.id}`,
          name: customTemplate.name,
          placeholder: customTemplate.prompt_parts[0].placeholder,
          text: customTemplate.prompt_parts[0].template,
        };
      });

      setCustomTemplateOptions(options);

      if (options[0]) {
        onCustomTemplateChange(options[0]);
        setValue('template_id', options[0]?.id);
      }
    }
  }, [customTemplatesData]);

  useEffect(() => {
    if (createConfigurationSettingsData) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
          finished: true,
        }),
      );

      deployConfigurationSettings({
        project_id: id,
        settings_id: createConfigurationSettingsData.id,
      }).then((deployConfigurationSettingsResult) => {
        if (!('error' in deployConfigurationSettingsResult)) {
          next();
        }
      });
    }
  }, [createConfigurationSettingsData]);

  const next = () => {
    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/control-desk?type=${ProductsType.customer}`
        : `${PATH_DASHBOARD.ROOT}`,
      { replace: true },
    );
  };

  const back = () => {
    navigate(getCreateProjectStepPath(id, +step - 1, projectTypeVar), { replace: true });
  };

  const submit = ({ text }: { text: string }) => {
    const prompt = {
      template_id: +selectedTemplate.id,
      input_data: {
        [selectedTemplate.placeholder]: text,
      },
    };

    createConfigurationSettings({ id, body: { prompt } });
  };

  const onCustomTemplateChange = (option?: CustomTemplateOption) => {
    if (option) {
      setInitialPlateValue(option.text);
      setValue('text', option.text);
      setSelectedTemplate(option);
    }
  };

  return (
    <div className={'w-full flex flex-col gap-5 items-center'}>
      <form
        id={'create-project-step4'}
        className={'w-full flex flex-col gap-5'}
        onSubmit={handleSubmit(submit)}
      >
        <CustomTemplateSelect
          control={control}
          label={'Template:'}
          options={customTemplateOptions}
          onChange={onCustomTemplateChange}
        />
      </form>
      <Controller
        control={control}
        name="text"
        render={({ field: { onChange }, fieldState: { error } }) => (
          <div className={'flex flex-col w-full gap-2.5'}>
            <PlateEditor
              label={'System Prompt:'}
              initialValue={initialPlateValue}
              onChange={onChange}
              error={error}
              containerClassName={'max-h-[470px] overflow-hidden bg-white'}
              className={'h-[calc(100%-50px)]'}
            />
          </div>
        )}
      />
      <Button
        form="create-project-step4"
        type="submit"
        className="min-w-24 w-full flex flex-row h-12"
      >
        Continue
      </Button>
      <button
        type="button"
        onClick={back}
        className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
      >
        ← Back
      </button>
    </div>
  );
};
